<template>
	<v-row class="pb-offset ma-0">
		<v-col class="pa-0">
			<v-card elevation="1" class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-3">Spazio disco</p>
					<v-spacer></v-spacer>
					<v-btn
						small
						outlined
						color="primary"
						:loading="refreshing"
						:disabled="loading || refreshing"
						@click="refresh"
					>
						<v-icon small class="mr-1">mdi-refresh</v-icon>Ricalcola ora
					</v-btn>
				</v-card-title>
				<v-divider></v-divider>

				<v-container class="pa-6" fluid>
					<div class="caption grey--text mb-4">
						Spazio occupato dall'intero tenant, misurato una volta al giorno. È diverso dalla quota
						per singola azienda mostrata in Area Clienti.
					</div>

					<div v-if="loading" class="text-center py-8">
						<v-progress-circular indeterminate color="primary"></v-progress-circular>
					</div>

					<template v-else>
						<!-- Mai misurato. Non si mostra se la lettura e' fallita:
						     li' `data` e' vuoto perche' non siamo riusciti a
						     leggere, non perche' la misura non sia mai stata
						     fatta, e dirlo insieme all'errore sarebbe falso. -->
						<v-alert v-if="!loadError && !data.measured_at" type="info" text class="mb-0">
							Lo spazio occupato non è ancora stato calcolato. Premi
							"Ricalcola ora" per avviare la prima misurazione.
						</v-alert>

						<template v-else>
							<!-- Quota non impostata: si mostra solo l'occupato -->
							<v-alert v-if="data.usage_percent === null" type="info" text dense class="mb-4">
								Nessun limite di spazio impostato per questo tenant.
							</v-alert>

							<div v-else class="mb-4">
								<div class="d-flex align-center mb-1">
									<span class="text-h5 font-weight-medium">{{ data.usage_percent }}%</span>
									<v-spacer></v-spacer>
									<span class="caption grey--text">
										{{ formatSize(data.used_bytes) }} di {{ data.max_gb }} GB
									</span>
								</div>
								<v-progress-linear
									:value="Math.min(data.usage_percent, 100)"
									:color="barColor"
									height="14"
									rounded
								></v-progress-linear>
								<div class="caption grey--text mt-1">
									{{ formatSize(data.free_bytes) }} liberi
								</div>
							</div>

							<div v-if="data.usage_percent === null" class="mb-4">
								<span class="text-h5 font-weight-medium">{{ formatSize(data.used_bytes) }}</span>
								<span class="caption grey--text ml-2">occupati</span>
							</div>

							<v-subheader class="pl-0">Ripartizione</v-subheader>
							<v-simple-table dense class="background-2">
								<tbody>
									<tr v-for="voce in breakdown" :key="voce.name">
										<td>{{ voce.label }}</td>
										<td class="text-right">{{ formatSize(voce.size) }}</td>
									</tr>
									<tr v-if="!breakdown.length">
										<td colspan="2" class="grey--text">Nessun dato</td>
									</tr>
								</tbody>
							</v-simple-table>

							<div class="caption grey--text mt-4">
								Ultimo calcolo: {{ formatDate(data.measured_at) }}
							</div>
						</template>
					</template>

					<v-alert v-if="message" :type="messageType" text dense class="mt-4 mb-0">
						{{ message }}
					</v-alert>
				</v-container>
			</v-card>
		</v-col>
	</v-row>
</template>

<script>
// Il job di ricalcolo (MeasureTenantStorageJob) ha timeout 900 secondi: su un
// tenant da 10-20 GB - cioe' esattamente quelli per cui questa tab esiste -
// la scansione supera spesso i 2 minuti. Il tetto del polling deve restare
// sotto i 900s del job ma dargli spazio a sufficienza, con un margine per il
// tempo che il job passa accodato prima di partire: 10 minuti.
const POLL_MAX_SECONDS = 600
// Primi 60 secondi di attesa: l'utente ha appena cliccato ed e' probabilmente
// ancora sulla pagina, quindi si aggiorna spesso. Dopo, l'intervallo si
// allunga: su 10 minuti totali, 5 secondi fissi vorrebbero dire ~120
// richieste per un calcolo che nella maggior parte dei casi l'utente non sta
// piu' guardando.
const POLL_INTERVAL_RAVVICINATO_MS = 5000
const POLL_INTERVAL_ESTESO_MS = 15000
const POLL_SOGLIA_INTERVALLO_ESTESO_SECONDS = 60
// Un blip di rete non e' improbabile su un polling che dura fino a 10
// minuti: si tollerano un paio di fallimenti consecutivi della GET prima di
// arrendersi e sbloccare il bottone. Un solo fallimento isolato non deve
// interrompere l'attesa.
const POLL_TENTATIVI_FALLITI_MAX = 3

export default {
	name: 'settings-storage',
	data() {
		return {
			loading: true,
			refreshing: false,
			data: {},
			message: '',
			messageType: 'info',
			pollTimer: null,
			pollElapsed: 0,
			pollFailures: 0,
			loadError: false
		}
	},
	computed: {
		barColor() {
			const pct = this.data.usage_percent || 0
			if (pct > 90) return 'red'
			if (pct >= 75) return 'orange'
			return 'green'
		},
		breakdown() {
			const raw = this.data.breakdown || {}
			return Object.keys(raw)
				.map(name => ({
					name: name,
					label: name === '_root' ? 'File nella radice' : name,
					size: raw[name]
				}))
				.sort((a, b) => b.size - a.size)
		}
	},
	created() {
		this.load()
	},
	beforeDestroy() {
		this.stopPolling()
	},
	methods: {
		// AppTabs renderizza la tab con `v-show="!$store.getters.progress"`, e
		// il router alza quel flag a ogni cambio rotta: finche' non lo si
		// riabbassa la tab resta montata ma invisibile. Ogni componente delle
		// Impostazioni deve abbassarlo quando ha finito di caricare — lo fa
		// anche SettingsOptionsMixin, che qui non usiamo perche' questa tab
		// non legge dalla tabella settings.
		endGlobalProgress() {
			this.$store.commit('endProgress')
		},
		load() {
			return this.axios.get('tenant-storage').then(response => {
				this.data = response.data.data || {}
				this.loading = false
				this.pollFailures = 0
				this.loadError = false
				this.endGlobalProgress()
			}).catch(() => {
				this.loading = false
				this.loadError = true
				this.endGlobalProgress()

				if (this.refreshing) {
					// Fallita mentre il polling e' in corso: non e' detto
					// che sia la fine del mondo (un blip di rete su 10
					// minuti di attesa e' plausibile), quindi non si blocca
					// subito il bottone. Solo dopo qualche fallimento
					// consecutivo ci si arrende, altrimenti l'utente restava
					// con lo spinner acceso fino al reload della pagina.
					this.pollFailures++
					if (this.pollFailures >= POLL_TENTATIVI_FALLITI_MAX) {
						this.stopPolling()
						this.refreshing = false
						this.messageType = 'error'
						this.message = 'Non riesco più a controllare lo stato del calcolo (problema di rete). ' +
							'Il calcolo potrebbe comunque essere ancora in corso sul server: ricarica la pagina per riprovare.'
					}
					return
				}

				this.messageType = 'error'
				this.message = 'Impossibile leggere lo spazio disco.'
			})
		},
		refresh() {
			if (this.refreshing) return
			this.message = ''
			this.refreshing = true
			const primaMisura = this.data.measured_at

			this.axios.post('tenant-storage/refresh').then(() => {
				this.messageType = 'info'
				this.message = 'Calcolo avviato, il dato si aggiorna fra poco.'
				this.startPolling(primaMisura)
			}).catch(e => {
				this.refreshing = false
				this.messageType = 'warning'
				this.message = (e.response && e.response.data && e.response.data.message) ||
					'Impossibile avviare il calcolo.'
			})
		},
		startPolling(primaMisura) {
			this.stopPolling()
			this.pollElapsed = 0
			this.pollFailures = 0
			this.scheduleNextPoll(primaMisura)
		},
		scheduleNextPoll(primaMisura) {
			const delayMs = this.pollElapsed >= POLL_SOGLIA_INTERVALLO_ESTESO_SECONDS
				? POLL_INTERVAL_ESTESO_MS
				: POLL_INTERVAL_RAVVICINATO_MS

			this.pollTimer = setTimeout(() => {
				this.pollElapsed += delayMs / 1000
				this.load().then(() => {
					// load() puo' aver gia' fermato tutto per troppi
					// fallimenti di rete consecutivi (vedi il suo catch):
					// in quel caso refreshing e' gia' false, non ripianificare.
					if (!this.refreshing) {
						return
					}

					if (this.data.measured_at && this.data.measured_at !== primaMisura) {
						this.stopPolling()
						this.refreshing = false
						this.messageType = 'success'
						this.message = 'Spazio disco aggiornato.'
						return
					}
					if (this.pollElapsed >= POLL_MAX_SECONDS) {
						this.stopPolling()
						this.refreshing = false
						this.messageType = 'info'
						this.message = 'Il calcolo sta ancora andando avanti: sui tenant più grandi può richiedere diversi minuti. ' +
							'Non hai perso nulla, torna su questa pagina fra qualche minuto per vedere il risultato aggiornato.'
						return
					}
					this.scheduleNextPoll(primaMisura)
				})
			}, delayMs)
		},
		stopPolling() {
			if (this.pollTimer) {
				clearTimeout(this.pollTimer)
				this.pollTimer = null
			}
		},
		formatSize(bytes) {
			if (bytes === null || bytes === undefined) return '-'
			if (!bytes) return '0 B'
			const units = ['B', 'KB', 'MB', 'GB', 'TB']
			let i = 0
			let size = Number(bytes)
			while (size >= 1024 && i < units.length - 1) {
				size /= 1024
				i++
			}
			return size.toFixed(i > 0 ? 1 : 0) + ' ' + units[i]
		},
		formatDate(iso) {
			if (!iso) return '-'
			return new Date(iso).toLocaleString('it-IT')
		}
	}
}
</script>
